import { produce } from 'immer';
import { Dispatch, SetStateAction, useCallback } from 'react';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import type { FlowType } from 'fable/flowEditor/components/flowEditorChrome';
import { AutomationNodeTypes } from 'hocs';
import { trackFlowActionSaved } from 'utils/flowEditor/tracking';

import { NodeT } from '../../types';
import { actionNodeDependencyOffset } from '../constants';
import { styles as nodeStyles } from '../renderers/node/nodeStyles';
import type { CanvasExtraData } from '../types/extraData';
import type { NodeAttributes } from '../types/node';
import type { Relation } from '../types/relation';
import { NodeState, NodeStateUpdateType } from '../types/state';
import {
  getChildOffset,
  getChildStartX,
  getChildStartY,
} from '../utils/canvas/calculateChildPosition/calculateChildPosition';
import {
  createConnection,
  createVisualConnection,
} from '../utils/node/getConnections/getConnections';
import { getNodeComponent } from '../utils/node/getNodeComponent/getNodeComponent';
import { getRootTree } from '../utils/node/getNodeTree/getNodeTree';
import { getScopeVars } from '../utils/scopeVars/getScopeVars/getScopeVars';

import { createAttachNode } from './node/useDisconnect/useDisconnect';

const nodetypeTrackingExceptions = [
  AutomationNodeTypes.CONDITION,
  AutomationNodeTypes.INIT,
  AutomationNodeTypes.QUEUE,
  AutomationNodeTypes.TAG,
  AutomationNodeTypes.WAIT,
  FlowNodeTypes.FILTERS,
  FlowNodeTypes.BUSINESS_HOURS,
];

type ReplaceNodeProps = {
  flowType: FlowType;
  setNodeState: Dispatch<SetStateAction<NodeState>>;
  extraData: CanvasExtraData;
  nodeAttributes: NodeAttributes;
};

type ReplaceNode = (props: ReplaceNodeProps) => (node: NodeT) => void;

export const useReplaceNode: ReplaceNode = ({
  flowType,
  setNodeState,
  extraData,
  nodeAttributes,
}) =>
  useCallback(
    (node) =>
      setNodeState(
        produce((state) => {
          const { connections, relations, changes } = state;

          changes.unshift(NodeStateUpdateType.NodeUpdate);

          const existingNode = { ...relations[node.id] };
          const newNode = { ...existingNode, ...node };
          const { parentId } = newNode;
          const { connections: existingDependencies } = existingNode;

          const dependencies = getNodeComponent(
            nodeAttributes,
            newNode,
          ).translatePayload(newNode, extraData);

          const dependenciesAsConnectionArray = dependencies.map(
            ({ id, connection }) => connection || id,
          );
          const dependenciesAsChildrenArray = dependencies.reduce<
            Relation['id'][]
          >((acc, { connection }) => {
            if (connection) {
              acc.push(connection);
            }

            return acc;
          }, []);

          existingDependencies.forEach((dep) => {
            const dependencyId = dep.connection || dep.id;
            const isDependencyDisconnected =
              dependenciesAsConnectionArray.indexOf(dependencyId) === -1;

            if (!isDependencyDisconnected) {
              return;
            }

            delete connections[dependencyId];

            // If dependency is a relation (i.e not a tag)
            if (dep.connection == null) {
              return;
            }

            // If node is not an action node we need to remove the parent since it's disconnected.
            if (relations[dependencyId].nodeType !== FlowNodeTypes.ACTION) {
              relations[dependencyId].parentId = null;
            } else {
              delete relations[dependencyId];
            }
          });

          dependencies.forEach((dependency) => {
            const { id, connection, tag, label } = dependency;

            const amountOfOuts = dependencies.length;
            const position =
              dependenciesAsConnectionArray.indexOf(connection || id) + 1;

            const offset = getChildOffset(amountOfOuts, position);

            /*
             * If there's no connection, then this is a visual node.
             */
            if (connection == null) {
              const { tag } = dependency;

              connections[id] = createVisualConnection({
                id,
                label,
                tag,
                parent: newNode,
                position: {
                  offset,
                  startX: newNode.pageX,
                  startY: newNode.pageY,
                },
              });

              return;
            }

            /*
             * If there is an existing connection
             * we just need to update the start position, offset, & label.
             */
            if (connection && connections[connection]) {
              const existingConnection = connections[connection];

              connections[connection] = {
                ...existingConnection,
                label,
                tag,
                offset: {
                  x: offset,
                  y: newNode.height,
                },
                startX: getChildStartX(newNode.pageX),
                startY: getChildStartY(newNode.pageY),
              };
            }

            if (connection && relations[connection]) {
              return;
            }

            const actionNode = createAttachNode({
              id: connection,
              parentId: newNode.id,
              pageX: newNode.pageX - nodeStyles.base.width / 2 + offset,
              pageY: newNode.pageY + actionNodeDependencyOffset,
              ...(label && { connectionLabel: label }),
            });

            relations[connection] = actionNode;
            connections[connection] = createConnection({
              relation: actionNode,
              parent: newNode,
              tag,
              position: {
                offset,
                startX: getChildStartX(newNode.pageX),
                startY: getChildStartY(newNode.pageY),
              },
            });
          });

          newNode.connections = dependencies;
          newNode.children = dependenciesAsChildrenArray;

          if (parentId) {
            const parentNode = relations[parentId];

            const updatedParent = getNodeComponent(
              nodeAttributes,
              parentNode,
            ).updateConnection(parentNode, newNode, newNode.id);

            relations[parentId] = updatedParent;
          }

          relations[node.id] = newNode;

          // We are planning to track the changes of some of the node modals
          // so this event will be triggered inside the modal in a few cases.
          if (!nodetypeTrackingExceptions.includes(newNode.nodeType)) {
            trackFlowActionSaved(!!existingNode.isAction, {
              flowType,
              nodeType: newNode.nodeType,
            });
          }

          state.scopeVars = getScopeVars(nodeAttributes, relations);
          state.rootTree = getRootTree(relations);
        }),
      ),
    [extraData, flowType, nodeAttributes, setNodeState],
  );
